// Diagnostics — trigger agent tasks and inspect agent_task_logs

import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { timeAgo } from "../lib/utils";

interface TaskLog {
  id: string;
  task_id: string;
  brand_id: string;
  user_id: string | null;
  provider: string;
  model: string | null;
  skills: string[] | null;
  required_context_files: string[] | null;
  player_prompt_preview: string | null;
  status: string;
  turns_used: number;
  duration_ms: number;
  findings_count: number;
  has_draft: boolean;
  has_reasoning: boolean;
  output_keys: string[] | null;
  outputs: Record<string, string> | null;
  error_detail: string | null;
  transcript_url: string | null;
  brand_doc_versions: Record<string, number> | null;
  created_at: string;
}

function statusVariant(status: string): "success" | "error" | "info" | "warning" {
  if (status === "approved") return "success";
  if (status === "error" || status === "blocked") return "error";
  if (status === "running") return "info";
  return "warning";
}

function formatMs(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60_000)}m ${Math.floor((ms % 60_000) / 1000)}s`;
}

function TranscriptModal({ fileId, onClose }: { fileId: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["transcript", fileId],
    queryFn: () => api.get<{ content: string }>(`/api/diagnostics/transcript/${fileId}`),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-16"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="flex max-h-[80vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-800">Transcript</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-5">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            </div>
          )}
          {error && (
            <p className="text-sm text-red-600">
              {error instanceof Error ? error.message : "Failed to load transcript"}
            </p>
          )}
          {data && (
            <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-slate-800">
              {data.content}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

function OutputsPanel({ outputs }: { outputs: Record<string, string> }) {
  const [activeKey, setActiveKey] = useState(() => Object.keys(outputs)[0] ?? "");
  const keys = Object.keys(outputs);
  if (keys.length === 0) return null;

  const content = outputs[activeKey] ?? "";
  let parsed: unknown = null;
  if (activeKey.endsWith(".json")) {
    try { parsed = JSON.parse(content); } catch { /* not valid json */ }
  }

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50">
      {/* File tabs */}
      <div className="flex gap-1 border-b border-slate-200 px-3 pt-2">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setActiveKey(k)}
            className={`rounded-t px-3 py-1 text-xs font-medium ${
              k === activeKey
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {k}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="max-h-96 overflow-auto p-3">
        {parsed !== null ? (
          <pre className="whitespace-pre-wrap font-mono text-xs text-slate-800">
            {JSON.stringify(parsed, null, 2)}
          </pre>
        ) : (
          <pre className="whitespace-pre-wrap font-mono text-xs text-slate-800">{content}</pre>
        )}
      </div>
    </div>
  );
}

export function Diagnostics() {
  const queryClient = useQueryClient();
  const [brandId, setBrandId] = useState("helloconvo-test");
  const [taskId, setTaskId] = useState("brand-research-diagnostic");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [transcriptFileId, setTranscriptFileId] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Diagnostics — HelloConvo";
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ["diagnostics-task-logs"],
    queryFn: () => api.get<{ data: TaskLog[] }>("/api/diagnostics/task-logs"),
    refetchInterval: 5_000,
  });

  const trigger = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; message: string }>("/api/diagnostics/run-task", { brandId, taskId }),
    onSuccess: () => {
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: ["diagnostics-task-logs"] }), 2_000);
    },
  });

  const logs = data?.data ?? [];

  return (
    <div className="space-y-6">
      {transcriptFileId && (
        <TranscriptModal fileId={transcriptFileId} onClose={() => setTranscriptFileId(null)} />
      )}

      <WorkspaceHeader
        eyebrow="Operator Workspace"
        title="Agent Task Diagnostics"
        description={(
          <>
            Trigger agent tasks and inspect every row saved to{" "}
            <code className="rounded bg-slate-100 px-1 text-xs">agent_task_logs</code>.
          </>
        )}
        breadcrumbs={[
          { label: "Workspace", href: "/home" },
          { label: "Diagnostics" },
        ]}
        metadata={(
          <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
            Task logs / transcripts
          </span>
        )}
      />

      {/* Trigger form */}
      <Card className="rounded-[20px] border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Trigger a Task</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">Brand ID</label>
            <input
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              value={brandId}
              onChange={(e) => setBrandId(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">Task ID</label>
            <input
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
            />
          </div>
          <button
            type="button"
            disabled={trigger.isPending || !brandId}
            onClick={() => trigger.mutate()}
            className="rounded-lg bg-slate-900 px-4 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {trigger.isPending ? "Triggering…" : "Run brand-research"}
          </button>
        </div>
        {trigger.isSuccess && (
          <p className="mt-2 text-xs text-green-700">{trigger.data?.message} Logs refresh every 5s.</p>
        )}
        {trigger.isError && (
          <p className="mt-2 text-xs text-red-700">
            {trigger.error instanceof Error ? trigger.error.message : "Failed to trigger task"}
          </p>
        )}
      </Card>

      {/* Logs */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">
            Task Logs{" "}
            <span className="font-normal text-slate-400">({logs.length} rows, newest first)</span>
          </h2>
          {isLoading && (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error instanceof Error ? error.message : "Failed to load logs"}
          </div>
        )}
        {!isLoading && logs.length === 0 && !error && (
          <p className="text-sm text-slate-500">No logs yet. Trigger a task above.</p>
        )}

        <div className="space-y-2">
          {logs.map((log) => (
            <Card key={log.id} className="overflow-hidden rounded-[20px] border border-slate-200 bg-white shadow-sm">
              {/* Summary row */}
              <button
                type="button"
                className="w-full px-4 py-3 text-left"
                onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={statusVariant(log.status)}>{log.status}</Badge>
                  <span className="font-mono text-sm font-medium text-slate-800">{log.task_id}</span>
                  <span className="text-xs text-slate-500">{log.brand_id}</span>
                  <span className="ml-auto flex items-center gap-3 text-xs text-slate-400">
                    <span>{log.model ?? log.provider}</span>
                    <span>{log.turns_used} turn{log.turns_used !== 1 ? "s" : ""}</span>
                    <span>{formatMs(log.duration_ms)}</span>
                    <span>{timeAgo(new Date(log.created_at))}</span>
                  </span>
                </div>
                {(log.status === "error" || log.status === "blocked") && log.error_detail && (
                  <p className="mt-1 truncate text-xs text-red-600">{log.error_detail}</p>
                )}
              </button>

              {/* Expanded detail */}
              {expandedId === log.id && (
                <div className="border-t border-slate-100 px-4 pb-4 pt-3">
                  {/* Meta grid */}
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs sm:grid-cols-3">
                    <Row label="ID" value={log.id} mono />
                    <Row label="User ID" value={log.user_id ?? "—"} mono />
                    <Row label="Provider" value={log.provider} />
                    <Row label="Model" value={log.model ?? "—"} mono />
                    <Row label="Findings" value={String(log.findings_count)} />
                    <Row label="Has draft" value={log.has_draft ? "yes" : "no"} />
                    <Row label="Has reasoning" value={log.has_reasoning ? "yes" : "no"} />
                    <Row label="Skills" value={(log.skills ?? []).join(", ") || "—"} />
                    <Row label="Required context" value={(log.required_context_files ?? []).join(", ") || "—"} />
                    <Row label="Output keys" value={(log.output_keys ?? []).join(", ") || "—"} />
                    <Row
                      label="Doc versions"
                      value={
                        log.brand_doc_versions
                          ? Object.entries(log.brand_doc_versions)
                              .map(([slug, ts]) => `${slug}:${ts}`)
                              .join(" ")
                          : "—"
                      }
                      mono
                    />
                  </dl>

                  {/* Prompt preview */}
                  {log.player_prompt_preview && (
                    <div className="mt-3">
                      <p className="mb-1 text-xs font-medium text-slate-500">Prompt / skill file</p>
                      <pre className="whitespace-pre-wrap rounded bg-slate-50 p-2 font-mono text-xs text-slate-700">
                        {log.player_prompt_preview}
                      </pre>
                    </div>
                  )}

                  {/* Error detail */}
                  {log.error_detail && (
                    <div className="mt-3">
                      <p className="mb-1 text-xs font-medium text-red-500">Error detail</p>
                      <pre className="whitespace-pre-wrap rounded bg-red-50 p-2 font-mono text-xs text-red-700">
                        {log.error_detail}
                      </pre>
                    </div>
                  )}

                  {/* Output files */}
                  {log.outputs && Object.keys(log.outputs).length > 0 && (
                    <div className="mt-3">
                      <p className="mb-1 text-xs font-medium text-slate-500">Output files</p>
                      <OutputsPanel outputs={log.outputs} />
                    </div>
                  )}

                  {/* Transcript */}
                  <div className="mt-3 flex items-center gap-3">
                    {log.transcript_url ? (
                      <>
                        <span className="font-mono text-xs text-slate-400">{log.transcript_url}</span>
                        <button
                          type="button"
                          onClick={() => setTranscriptFileId(log.transcript_url)}
                          className="rounded-lg bg-slate-900 px-3 py-1 text-xs font-medium text-white hover:bg-slate-700"
                        >
                          View transcript
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">No transcript stored</span>
                    )}
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="font-medium text-slate-500">{label}</dt>
      <dd className={`mt-0.5 break-all text-slate-800 ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}
